import { useFormik } from 'formik';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SetDesiredAmountModal } from '@/modals/SetDesiredAmountModal';
import { ConfirmApplicationModal } from '@/modals/ConfirmApplicationModal';
import { useApplicationStatusByFundraisingId } from '@/hooks/shared/useApplicationStatusByFundraisingId';
import { useGetFundraisingStatusById } from '@/query/queries/fundraising/useGetFundraisingStatusById';
import { prettyFloatMoney } from '@/helpers/utils';
import { BackButton } from '@/components/BackButton';
import { useModal } from '@/providers/modal';
import { RefinProvider } from '@/providers/refin';
import { Toggle } from '@/webkit/form/Toggle';
import { FR_STEPS } from '@/helpers/fundraising';
import { DATE_FORMAT_DD_MMM_YYYY } from '@/helpers/common';
import { isAfter, prettyDate } from '@scramble/utils';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { ApplyRefinButton } from './apply';
import { StepResult } from '../StepResult';
import IconEdit from './icon_edit.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

export const FundraisingGoalStructure = ({ selectedApplication, onBack }) => {
  const { openSingleModal } = useModal();
  const { t } = useTranslation('fundraising');
  const fundraisingId = selectedApplication.fundraising_id;
  const { application, refetch } = useApplicationStatusByFundraisingId(fundraisingId);
  const { data: status } = useGetFundraisingStatusById(fundraisingId);

  const { values, setFieldValue, handleSubmit } = useFormik({
    enableReinitialize: true,
    initialValues: {
      new_funds: application?.new_funds || 0,
      new_funds_checked: !application?.refinancings.length,
      refinancing:
        application?.refinancings
          .filter(refin => refin.checked)
          .map(refin => refin.fundraising_id) || [],
    },
  });

  if (!status) return null;

  const applyAvailable =
    application?.applicable_from &&
    isAfter({
      targetDate: application?.applicable_from,
    });

  const canApply =
    applyAvailable &&
    ((Number(values.new_funds) && values.new_funds_checked) || values.refinancing.length > 0);

  return (
    <form className={s.form} onSubmit={handleSubmit}>
      <div className="container-inner">
        <BackButton onClick={onBack} />
        <h1 className="f-hero-h2">{selectedApplication.fundraising_name} round</h1>
        {status.step === FR_STEPS.fundraising_result && <StepResult status={status} />}
        <div className={s.application_status}>
          <div className={s.application_status__content}>
            <div className="f-article-accent">
              {applyAvailable
                ? t('fr_goal_structure.apply_for_fundraising', {
                    fundraising_name: application?.fundraising_name,
                  })
                : t('fr_goal_structure.unavailable_title', {
                    fundraising_name: application?.fundraising_name,
                  })}
            </div>
            <div className="f-body-regular">
              {applyAvailable
                ? t('fr_goal_structure.choose_participation_type')
                : t('fr_goal_structure.unavailable_desc', {
                    date: prettyDate(application?.applicable_from, DATE_FORMAT_DD_MMM_YYYY),
                  })}
            </div>
          </div>
          {applyAvailable ? (
            <div className={s.application_status__badge}>
              {t('fr_goal_structure.apply_by', {
                date: prettyDate(application?.applicable_to, 'MMMM DD'),
              })}
            </div>
          ) : (
            <div className={s.application_status__badge_unavailable}>
              {t('fr_goal_structure.unavailable_badge')}
            </div>
          )}
        </div>

        <div className={s.goal_structure}>
          <div className={s.goal_structure__content}>
            <div className="f-article-accent">{t('fr_goal_structure.goal_structure')}</div>
            <div className="f-small-regular" style={{ color: 'var(--Grey-900-Deep, #52626F)' }}>
              {t('fr_goal_structure.structure_definition')}
            </div>
          </div>
          <button
            type="button"
            className={classnames(r.yellow_base, s.goal_structure__btn_apply)}
            onClick={() => openSingleModal(MODAL_IDS.confirm_application_modal)}
            disabled={!canApply}
          >
            {t('fr_goal_structure.apply')}
          </button>
        </div>

        <div className={s.funds}>
          <div className="f-body-accent">{t('fr_goal_structure.funds')}</div>
          <div className={s.funds__info}>
            <div className="f-small-accent">{t('fr_goal_structure.new_funds')}</div>
            <div className={classnames(s.funds__value, 'f-small-regular')}>
              {values.new_funds ? (
                <span style={{ color: 'var(--Black-600-Semi-Dark, #17232D)' }}>
                  {prettyFloatMoney(values.new_funds)}
                </span>
              ) : (
                <span style={{ color: 'var(--Grey-900-Deep, #52626F)' }}>
                  {t('fr_goal_structure.set_desired_amount')}
                </span>
              )}
            </div>
            <div>
              <button
                type="button"
                className={s.funds__btn_edit}
                onClick={() => applyAvailable && openSingleModal(MODAL_IDS.set_desired_amount)}
              >
                <img src={IconEdit} alt="edit" width={20} height={20} />
              </button>
            </div>
            <div>
              <Toggle
                name="new_funds"
                checked={values.new_funds_checked}
                onChange={() => setFieldValue('new_funds_checked', !values.new_funds_checked)}
              />
            </div>
          </div>
        </div>

        {application?.refinancings.length > 0 && (
          <div className={s.refinancing}>
            <div className="f-body-accent">{t('fr_goal_structure.refinancing')}</div>
            <div className={s.refinancing__content}>
              <RefinProvider disableReload={true}>
                {application?.refinancings.map(refin => (
                  <div className={s.refinancing__info} key={refin.fundraising_id}>
                    <div className="f-small-accent">{refin.title}</div>
                    <div className={classnames(s.refinancing__value, 'f-small-regular')}>
                      {prettyFloatMoney(refin.amount)}
                    </div>
                    <div className={s.refinancing__last}>
                      {refin.checked ? (
                        <Toggle
                          name={refin.title}
                          disabled={refin.checked}
                          className={s.refinancing__toggle}
                          checked={values.refinancing.includes(refin.fundraising_id)}
                          onChange={() =>
                            setFieldValue(
                              'refinancing',
                              values.refinancing.includes(refin.fundraising_id)
                                ? values.refinancing.filter(id => id !== refin.fundraising_id)
                                : [...values.refinancing, refin.fundraising_id],
                            )
                          }
                        />
                      ) : (
                        <ApplyRefinButton
                          loan={refin.loan}
                          onSuccess={async refin => {
                            await refetch();
                            setFieldValue('refinancing', [
                              ...values.refinancing,
                              refin.fundraising_id,
                            ]);
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </RefinProvider>
            </div>
          </div>
        )}
      </div>

      <>
        <SetDesiredAmountModal
          amount={values.new_funds}
          onChange={amount => setFieldValue('new_funds', amount)}
        />
        <ConfirmApplicationModal
          newFunds={values.new_funds}
          refinancing={values.refinancing}
          fundraisingId={selectedApplication.fundraising_id}
        />
      </>
    </form>
  );
};
